import templateData from '@shared/data/interview-guide-template.json';
import type { InterviewGuideTemplate } from '@shared/guide-template';
import type { InterviewSession } from '@shared/types.ts';

export interface GuideSectionSummary {
  id: string;
  title: string;
  estimatedMinutes: number;
  blockCount: number;
  sessionBreakpoint: boolean;
}

export interface GuideSummary {
  name: string;
  description: string | null;
  status: string;
  version: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  structureId: string;
  sectionCount: number;
  blockCount: number;
  requiredBlockCount: number;
  optionalBlockCount: number;
  estimatedMinutes: number;
  sessionCount: number;
}

const templateGuide = templateData as InterviewGuideTemplate;

export function getTemplateGuide(): InterviewGuideTemplate {
  return templateGuide;
}

export function buildGuideSummary(guide: InterviewGuideTemplate): {
  summary: GuideSummary;
  sections: GuideSectionSummary[];
} {
  const sections = Array.isArray(guide.sections) ? guide.sections : [];
  const blocks = guide.blocks ?? {};

  const sectionSummaries: GuideSectionSummary[] = sections
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((section) => ({
      id: section.section_id,
      title: section.title,
      estimatedMinutes: section.estimated_minutes ?? 0,
      blockCount: Array.isArray(section.block_ids) ? section.block_ids.length : 0,
      sessionBreakpoint: Boolean(section.session_breakpoint),
    }));

  const blockIds = sections.flatMap((section) =>
    Array.isArray(section.block_ids) ? section.block_ids : []
  );
  const uniqueBlockIds = Array.from(new Set(blockIds));
  const requiredBlockCount = uniqueBlockIds.filter((id) => blocks[id]?.required).length;
  const optionalBlockCount = Math.max(0, uniqueBlockIds.length - requiredBlockCount);
  const estimatedMinutes = sections.reduce(
    (total, section) => total + (section.estimated_minutes ?? 0),
    0
  );
  const sessionBreakpoints = sections.filter((section) => section.session_breakpoint).length;
  const sessionCount = sections.length > 0 ? sessionBreakpoints + 1 : 0;

  return {
    summary: {
      name: guide.name,
      description: guide.description ?? null,
      status: guide.status ?? 'draft',
      version: guide.version ?? 'v1.0',
      createdAt: guide.created_at ?? '',
      updatedAt: guide.updated_at ?? '',
      createdBy: guide.created_by ?? 'unknown',
      structureId: guide.structure_id,
      sectionCount: sections.length,
      blockCount: uniqueBlockIds.length,
      requiredBlockCount,
      optionalBlockCount,
      estimatedMinutes,
      sessionCount,
    },
    sections: sectionSummaries,
  };
}

/**
 * Build an InterviewGuideTemplate from a session's structureData.
 * This lets us reuse buildGuideSummary for real session data,
 * not just the static fallback template.
 */
export function buildGuideFromSession(session: InterviewSession): InterviewGuideTemplate | null {
  const data = session.structureData as Record<string, unknown> | undefined;
  if (!data) return null;

  const sections = Array.isArray(data.sections) ? data.sections : [];
  const blocks = (data.blocks && typeof data.blocks === 'object' && !Array.isArray(data.blocks))
    ? data.blocks as Record<string, { id?: string; name?: string; question?: string; required?: boolean; estimatedMinutes?: number }>
    : {};

  return {
    structure_id: (data.structure_id as string) ?? '',
    version: session.structureVersion ?? '1.0',
    name: session.structureName ?? session.sessionName ?? 'Interview',
    description: (data.description as string) ?? undefined,
    designer_context: (data.designer_context as string) ?? undefined,
    status: 'active',
    sections: sections as InterviewGuideTemplate['sections'],
    blocks: blocks as InterviewGuideTemplate['blocks'],
  };
}
